import { type FC } from "react";

import { Checkbox } from "mds/components/Checkbox";
import { ListItem } from "mds/components/ListItem";
import { FolderIcon } from "mds/icons";
import { courseGroupingSelectors, courseSelectors } from "store/selectors";
import { useObjectSelector } from "store/store-hooks";

type CourseSelectionListItemProps = {
  courseId: string;
  isChecked: boolean;
  onToggle: (courseId: string) => void;
};

const CourseSelectionListItem: FC<CourseSelectionListItemProps> = ({
  courseId,
  isChecked,
  onToggle,
}) => {
  const course = useObjectSelector(
    (s) => courseSelectors.selectById(s, courseId),
    ["code", "grouping_id"],
  );
  const grouping = useObjectSelector(
    (s) => courseGroupingSelectors.selectById(s, course?.grouping_id),
    ["title"],
  );

  if (!course) {
    return null;
  }

  return (
    <div>
      <ListItem className="w-full gap-2">
        <Checkbox checked={isChecked} className="w-full" onChange={() => onToggle(courseId)}>
          <span className="flex w-full items-center justify-between">
            <span>{course.code}</span>

            {grouping?.title && (
              <span className="flex items-center gap-1">
                <FolderIcon className="size-4 text-black-tint-70" />
                <span>{grouping.title}</span>
              </span>
            )}
          </span>
        </Checkbox>
      </ListItem>
    </div>
  );
};

type CourseSelectionListProps = {
  courseIds: string[];
  selectedCourseIds: string[];
  onCourseToggle: (courseId: string) => void;
};

export const CourseSelectionList: FC<CourseSelectionListProps> = ({
  courseIds,
  selectedCourseIds,
  onCourseToggle,
}) => {
  const selectedCourseIdSet = new Set(selectedCourseIds);

  if (courseIds.length === 0) {
    return null;
  }

  return (
    <div className="max-h-[150px] overflow-y-scroll rounded-lg border border-black-tint-90 p-2">
      {courseIds.map((courseId) => (
        <CourseSelectionListItem
          courseId={courseId}
          isChecked={selectedCourseIdSet.has(courseId)}
          key={courseId}
          onToggle={onCourseToggle}
        />
      ))}
    </div>
  );
};
